'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useRef, useState } from 'react'
import {
  CONTACT_FIELD_LIMITS,
  CONTACT_TOPICS,
  SUPPORT_EMAIL,
  SUPPORT_GMAIL_COMPOSE,
  SUPPORT_MAILTO,
} from '@kiao-voice/core'
import { Trans, useTranslation } from 'react-i18next'
import { Button, ButtonLink, Field, Input, Picker, Textarea } from '@kiao-voice/ui-kit'
import {
  ContactSubmitError,
  submitContactMessage,
  type ContactTopic,
} from '../../features/contact/contact-client'

/** A `Record`, so a topic added to `CONTACT_TOPICS` fails to compile here until it has a label. */
const TOPIC_LABEL_KEYS: Record<ContactTopic, string> = {
  product: 'web:contact.topicProduct',
  billing: 'web:contact.topicBilling',
  partnership: 'web:contact.topicPartnership',
  other: 'web:contact.topicOther',
}

/** Deliberately loose — the API validates authoritatively. This only catches the
 *  typo before it costs the visitor a rate-limit slot. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface FieldErrors {
  name?: string
  email?: string
  message?: string
}

/**
 * The `/contact` page body: a readable support address with three ways to open a
 * message, what to expect for a reply, and a form that needs no mail client at
 * all.
 *
 * Why this page exists instead of a bare `mailto:` link — reported as ClickUp
 * 86eyh4nkx: a `mailto:` is only as good as the visitor's OS mail-handler
 * config. On Windows with no default mail app it opens the protocol chooser,
 * where picking a browser navigates to the `mailto:` URL and renders a blank
 * tab, and picking an unconfigured Outlook silently does nothing. Anyone living
 * in webmail has no handler registered either. So every path here is
 * independent: the address is plain selectable text, the copy button needs only
 * the clipboard, the Gmail link needs only a browser, and the form needs
 * neither.
 */
export function ContactPanel() {
  const { t } = useTranslation(['web'])

  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [topic, setTopic] = useState<ContactTopic>('product')
  const [message, setMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Focus targets so a failed submit lands the caret on the first problem
  // instead of leaving a screen-reader user on the Send button with no idea why
  // nothing happened.
  const nameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const messageRef = useRef<HTMLTextAreaElement>(null)

  const copyAddress = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL)
      setCopyState('copied')
    } catch (error) {
      reportHandledException(error, { source: 'components/contact/contact-panel.tsx' })
      // Clipboard access can be denied outright (permissions, an insecure
      // context, an older browser). Say so and point at the address that is
      // already on screen — silently doing nothing would look like the same
      // dead end this page exists to remove.
      console.warn('Clipboard write failed — falling back to manual selection', error)
      setCopyState('failed')
    }
  }, [])

  const validate = useCallback((): FieldErrors => {
    const errors: FieldErrors = {}
    // Length caps come from CONTACT_FIELD_LIMITS, the same constants the API
    // validator uses — when the two drifted, an over-long field passed here and
    // came back as an undiagnosable generic error.
    if (!name.trim()) {
      errors.name = t('web:contact.errorNameRequired')
    } else if (name.trim().length > CONTACT_FIELD_LIMITS.name) {
      errors.name = t('web:contact.errorTooLong', { max: CONTACT_FIELD_LIMITS.name })
    }
    if (!email.trim()) {
      errors.email = t('web:contact.errorEmailRequired')
    } else if (!EMAIL_PATTERN.test(email.trim())) {
      errors.email = t('web:contact.errorEmailInvalid')
    } else if (email.trim().length > CONTACT_FIELD_LIMITS.email) {
      errors.email = t('web:contact.errorTooLong', { max: CONTACT_FIELD_LIMITS.email })
    }
    if (!message.trim()) {
      errors.message = t('web:contact.errorMessageRequired')
    } else if (message.trim().length > CONTACT_FIELD_LIMITS.message) {
      errors.message = t('web:contact.errorTooLong', { max: CONTACT_FIELD_LIMITS.message })
    }
    return errors
  }, [email, message, name, t])

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setSubmitError(null)

      const errors = validate()
      setFieldErrors(errors)
      if (Object.keys(errors).length > 0) {
        const firstInvalid = errors.name ? nameRef : errors.email ? emailRef : messageRef
        firstInvalid.current?.focus()
        return
      }

      setStatus('submitting')
      try {
        await submitContactMessage({
          name: name.trim(),
          email: email.trim(),
          topic,
          message: message.trim(),
          // Hidden field — a value here means a bot filled it, and the API drops
          // the submission. Only sent when non-empty so a real send stays clean.
          ...(honeypot ? { website: honeypot } : {}),
        })
        setStatus('success')
      } catch (error) {
        reportHandledException(error, { source: 'components/contact/contact-panel.tsx' })
        setStatus('idle')
        // Localized copy per failure, never the raw server text: a rate limit
        // means wait, a provider outage means retry or email us directly.
        const code = error instanceof ContactSubmitError ? error.code : 'unknown'
        // Logged as well as shown: the visible copy is the user's recovery, but a
        // network error or an unexpected status otherwise leaves no diagnostic
        // trail at all. Only the code goes out — never the name, address or
        // message the visitor typed.
        console.warn('contact: submit failed', { code })
        if (code === 'rate_limited') {
          setSubmitError(t('web:contact.errorRateLimited'))
        } else if (code === 'send_failed') {
          setSubmitError(t('web:contact.errorSendFailed', { email: SUPPORT_EMAIL }))
        } else if (code === 'invalid') {
          // The form validates the same limits the API does, so a 422 means the
          // two drifted — say which kind of problem it is rather than falling
          // through to the generic copy.
          setSubmitError(t('web:contact.errorInvalid'))
        } else {
          setSubmitError(t('web:contact.errorUnknown', { email: SUPPORT_EMAIL }))
        }
      }
    },
    [email, honeypot, message, name, t, topic, validate]
  )

  return (
    <section className="container mx-auto max-w-[640px] px-5 py-16 md:py-24">
      <h1 className="text-[length:var(--fs-contact-hero)] leading-tight">
        {t('web:contact.heading')}
      </h1>
      <p className="mt-4 text-[length:var(--text-md)] text-[var(--muted)]">
        {t('web:contact.intro')}
      </p>

      <div className="mt-10 border-t border-foreground pt-8">
        <span className="text-[length:var(--text-sm)] text-[var(--muted)]">
          {t('web:contact.emailLabel')}
        </span>
        <p className="mt-1 text-[length:var(--fs-contact-heading)] select-all">{SUPPORT_EMAIL}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={copyAddress}>
            {copyState === 'copied' ? t('web:contact.copied') : t('web:contact.copy')}
          </Button>
          <ButtonLink
            variant="secondary"
            size="sm"
            href={SUPPORT_GMAIL_COMPOSE}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('web:contact.openGmail')}
          </ButtonLink>
          <ButtonLink variant="ghost" size="sm" href={SUPPORT_MAILTO}>
            {t('web:contact.openMailApp')}
          </ButtonLink>
        </div>
        {copyState === 'failed' ? (
          <p className="mt-3 text-[length:var(--text-sm)] text-error" role="alert">
            {t('web:contact.copyFailed')}
          </p>
        ) : null}
      </div>

      <div className="mt-10 border-t border-foreground pt-8">
        <h2 className="text-[length:var(--fs-contact-heading)]">{t('web:contact.hoursHeading')}</h2>
        <p className="mt-3 text-[length:var(--text-md)] text-[var(--muted)]">
          {t('web:contact.hoursBody')}
        </p>
        <p className="mt-2 text-[length:var(--text-md)] text-[var(--muted)]">
          {t('web:contact.hoursFollowUp')}
        </p>
        <p className="mt-4 text-[length:var(--text-md)] text-[var(--muted)]">
          <Trans
            t={t}
            i18nKey="web:contact.faqPrompt"
            components={{
              faq: <a href="/pricing" className="underline underline-offset-[3px]" />,
            }}
          />
        </p>
      </div>

      <div className="mt-10 border-t border-foreground pt-8">
        {status === 'success' ? (
          <div role="status">
            <h2 className="text-[length:var(--fs-contact-heading)]">
              {t('web:contact.successHeading')}
            </h2>
            <p className="mt-3 text-[length:var(--text-md)] text-[var(--muted)]">
              {t('web:contact.successBody')}
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-[length:var(--fs-contact-heading)]">
              {t('web:contact.formHeading')}
            </h2>
            <form className="mt-5 flex flex-col gap-5" onSubmit={onSubmit} noValidate>
              <Field
                label={t('web:contact.nameLabel')}
                htmlFor="contact-name"
                error={fieldErrors.name ?? null}
                errorId="contact-name-error"
              >
                <Input
                  id="contact-name"
                  name="name"
                  ref={nameRef}
                  required
                  aria-required="true"
                  maxLength={CONTACT_FIELD_LIMITS.name}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                />
              </Field>

              <Field
                label={t('web:contact.emailFieldLabel')}
                htmlFor="contact-email"
                error={fieldErrors.email ?? null}
                errorId="contact-email-error"
                hint={t('web:contact.emailFieldHint')}
                hintId="contact-email-hint"
              >
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  ref={emailRef}
                  required
                  aria-required="true"
                  maxLength={CONTACT_FIELD_LIMITS.email}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-describedby={
                    fieldErrors.email ? 'contact-email-error' : 'contact-email-hint'
                  }
                />
              </Field>

              <Field label={t('web:contact.topicLabel')} htmlFor="contact-topic">
                <Picker
                  id="contact-topic"
                  label={t('web:contact.topicLabel')}
                  value={topic}
                  onChange={(value) => setTopic(value as ContactTopic)}
                  options={CONTACT_TOPICS.map((value) => ({
                    value,
                    label: t(TOPIC_LABEL_KEYS[value]),
                  }))}
                />
              </Field>

              <Field
                label={t('web:contact.messageLabel')}
                htmlFor="contact-message"
                error={fieldErrors.message ?? null}
                errorId="contact-message-error"
              >
                <Textarea
                  id="contact-message"
                  name="message"
                  ref={messageRef}
                  required
                  aria-required="true"
                  maxLength={CONTACT_FIELD_LIMITS.message}
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                />
              </Field>

              {/* Honeypot. Hidden from sight and from assistive tech, and out of
                  the tab order, so a human never reaches it — anything here came
                  from a bot filling every input on the page.

                  The label is deliberately NOT run through i18n: it is bait read
                  only by scrapers, never announced (the wrapper is aria-hidden)
                  and never rendered, so it is not user-facing copy. Translating
                  it would also make the trap locale-dependent. */}
              <div aria-hidden="true" className="absolute h-0 w-0 overflow-hidden opacity-0">
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {submitError ? (
                <p className="text-[length:var(--text-sm)] text-error" role="alert">
                  {submitError}
                </p>
              ) : null}

              <Button type="submit" className="self-start" disabled={status === 'submitting'}>
                {status === 'submitting' ? t('web:contact.submitting') : t('web:contact.submit')}
              </Button>
            </form>
          </>
        )}
      </div>
    </section>
  )
}
